import React from 'react';
import { cx } from './cx';
import { wfChipFontStyle } from '@/styles/typography';
import { playClickSound } from '@/utils/soundUtils';

export interface FilterChipProps {
  active?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  className?: string;
  /** Pill (default) or rounded box — matches games toolbar controls. */
  shape?: 'pill' | 'box';
  /** Box only — fixed width so short labels (PLAY, All, HOT) align. */
  uniform?: boolean;
  /** Skip automatic click sound. */
  silent?: boolean;
  'aria-label'?: string;
}

/** Wireframe `.chip` — pad 4×12, font 11 × --proto-scale */
export default function FilterChip({
  active,
  onClick,
  children,
  className,
  shape = 'pill',
  uniform = false,
  silent = false,
  'aria-label': ariaLabel,
}: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={(e) => {
        if (!silent) playClickSound();
        onClick?.(e);
      }}
      aria-pressed={active}
      aria-label={ariaLabel}
      className={cx(
        'font-ui font-semibold whitespace-nowrap shrink-0',
        'transition-[background,color,box-shadow,border-color] duration-150 focus-visible:outline-none focus-visible:shadow-focus-ring',
        'active:scale-[0.97]',
        shape === 'box'
          ? cx(
              'cosmos-filter-chip-box',
              uniform ? 'w-[5.2rem] min-w-[5.2rem]' : 'min-w-[5.2rem]'
            )
          // ponytail: 999px, không `rounded-full` — bán kính vô cực (calc(infinity*1px))
          // khiến Chrome fringe 2 đầu gradient .cosmos-beam thành vệt oval cyan/magenta.
          // Khớp chuẩn codebase (globals.css dùng 999px ở mọi pill).
          : 'rounded-[999px]',
        active
          ? 'cosmos-filter-chip--active'
          : 'border border-border-ice bg-transparent text-ice hover:bg-white/10 hover:text-ink',
        className
      )}
      style={{
        padding: shape === 'box' ? undefined : 'var(--wf-chip-py) var(--wf-chip-px)',
        ...wfChipFontStyle,
      }}
    >
      {children}
    </button>
  );
}
